import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  CircularProgress,
  Grid,
  IconButton,
} from "@mui/material";
import {
  FiUpload,
  FiArrowDownCircle,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import Papa from "papaparse";
import { contactsAPI } from "../services/api";
import { showSuccess, showError } from "../utils/toast";
import { colors } from "../theme/modernTheme";
import { ArrowBack } from "@mui/icons-material";

const MAX_ROWS = 2000;

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [errorRows, setErrorRows] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0] || e.dataTransfer?.files?.[0];
    if (
      selectedFile &&
      (selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv"))
    ) {
      setFile(selectedFile);
      setResults(null);
      setErrorRows([]);
    } else if (selectedFile) {
      showError("Please select a valid CSV file");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileChange(e);
  };

  const validateRow = (row, index) => {
    const errors = [];
    const type = row.type?.toUpperCase();

    if (!type || !["INDIVIDUAL", "ORGANIZATION"].includes(type)) {
      errors.push("Invalid type (must be INDIVIDUAL or ORGANIZATION)");
    }

    if (type === "INDIVIDUAL" && !row.first_name) {
      errors.push("First name is required for Individuals");
    }

    if (type === "ORGANIZATION" && !row.organization_name) {
      errors.push("Organization name is required for Organizations");
    }

    // Check if either email or phone is provided
    const hasEmail = row.primary_email && row.primary_email.trim() !== "";
    const hasPhone = row.phone_1 && row.phone_1.trim() !== "";

    if (!hasEmail && !hasPhone) {
      errors.push("Either Primary Email or Primary Phone is required");
    }

    // Validate email format if provided
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.primary_email)) {
      errors.push("Invalid email format");
    }

    return errors;
  };

  const processUpload = () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;

        if (rows.length > MAX_ROWS) {
          showError(`Maximum ${MAX_ROWS} rows allowed`);
          setUploading(false);
          return;
        }

        const validContacts = [];
        const invalidRows = [];

        rows.forEach((row, index) => {
          const errors = validateRow(row, index);
          if (errors.length > 0) {
            invalidRows.push({ ...row, errorMessage: errors.join("; ") });
          } else {
            // Clean/Normalise data
            const cleanedRow = { ...row };

            // Generate derived fields
            if (cleanedRow.first_name || cleanedRow.last_name) {
              cleanedRow.full_name = `${cleanedRow.first_name || ""} ${
                cleanedRow.last_name || ""
              }`.trim();
            }

            // Normalize phone numbers - ensure country code and 10-digit number are separate
            if (cleanedRow.phone_1_country_code && cleanedRow.phone_1) {
              // Remove + prefix if present (we store without +)
              cleanedRow.phone_1_country_code =
                cleanedRow.phone_1_country_code.replace(/^\+/, "");
              // Remove any non-digits from number (should be 10 digits)
              cleanedRow.phone_1 = cleanedRow.phone_1.replace(/\D/g, "");
            }

            if (cleanedRow.phone_2_country_code && cleanedRow.phone_2) {
              // Remove + prefix if present (we store without +)
              cleanedRow.phone_2_country_code =
                cleanedRow.phone_2_country_code.replace(/^\+/, "");
              // Remove any non-digits from number (should be 10 digits)
              cleanedRow.phone_2 = cleanedRow.phone_2.replace(/\D/g, "");
            }

            validContacts.push(cleanedRow);
          }
        });

        if (validContacts.length === 0 && invalidRows.length > 0) {
          setResults({
            total: rows.length,
            success: 0,
            failed: invalidRows.length,
          });
          setErrorRows(invalidRows);
          setUploading(false);
          return;
        }

        const batchSize = 100;
        let successCount = 0;
        let failedFromBackend = [];

        try {
          for (let i = 0; i < validContacts.length; i += batchSize) {
            const batch = validContacts.slice(i, i + batchSize);
            const response = await contactsAPI.bulkUpload(batch);

            successCount += response.data.success_count;
            if (response.data.errors && response.data.errors.length > 0) {
              failedFromBackend = [
                ...failedFromBackend,
                ...response.data.errors.map((err) => ({
                  ...err.contact,
                  errorMessage: err.message,
                })),
              ];
            }

            setProgress(
              Math.round(((i + batch.length) / validContacts.length) * 100)
            );
          }

          const totalFAILED = invalidRows.length + failedFromBackend.length;
          setResults({
            total: rows.length,
            success: successCount,
            failed: totalFAILED,
          });
          setErrorRows([...invalidRows, ...failedFromBackend]);

          if (successCount > 0) {
            showSuccess(`Successfully uploaded ${successCount} contacts`);
          }
        } catch (err) {
          showError("An error occurred during bulk upload");
        } finally {
          setUploading(false);
        }
      },
      error: (err) => {
        showError("Failed to parse CSV file");
        setUploading(false);
      },
    });
  };

  const downloadErrorCsv = () => {
    if (errorRows.length === 0) return;
    const csv = Papa.unparse(errorRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `upload_errors_${new Date().getTime()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSampleTemplate = () => {
    const sampleData = [
      {
        type: "INDIVIDUAL",
        first_name: "John",
        last_name: "Doe",
        organization_name: "",
        primary_email: "john.doe@example.com",
        phone_1_country_code: "91",
        phone_1: "9876543210",
        address_line1: "123 Street Name",
        city: "Mumbai",
        state: "Maharashtra",
        postal_code: "400001",
        country: "India",
        tags: "VIP, Donor",
        notes: "INDIVIDUAL requires first_name",
      },
      {
        type: "ORGANIZATION",
        first_name: "",
        last_name: "",
        organization_name: "Tech Solutions Ltd",
        primary_email: "info@techsolutions.com",
        phone_1_country_code: "91",
        phone_1: "9123456789",
        address_line1: "456 Corporate Hub",
        city: "Bangalore",
        state: "Karnataka",
        postal_code: "560001",
        country: "India",
        tags: "Corporate",
        notes: "ORGANIZATION requires organization_name",
      },
      {
        type: "INDIVIDUAL",
        first_name: "Priya",
        last_name: "Sharma",
        organization_name: "",
        primary_email: "",
        phone_1_country_code: "91",
        phone_1: "9988776655",
        address_line1: "789 Park Avenue",
        city: "Delhi",
        state: "Delhi",
        postal_code: "110001",
        country: "India",
        tags: "WhatsApp",
        notes:
          "Email OR phone is required (phone format: country code + 10 digits)",
      },
    ];
    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "contact_upload_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const reset = () => {
    setFile(null);
    setUploading(false);
    setProgress(0);
    setResults(null);
    setErrorRows([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          mt: 1,
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate("/contacts")}
            size="small"
            sx={{
              "&:hover": { color: "rgba(0, 0, 0, 1)" },
            }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: colors.text.primary,
            }}>
            Bulk Contact Upload
          </Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          p: 6,
          borderRadius: "4px",
          width: "100%",

          backgroundColor: "#fff",
        }}>
        {!results && !uploading && (
          <Box
            sx={{
              border: `2px dashed ${
                isDragging
                  ? colors.primary.main
                  : file
                  ? colors.success.main
                  : colors.border.main
              }`,
              borderRadius: 1,
              p: 8,
              minHeight: "450px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: isDragging
                ? colors.primary.main + "08"
                : file
                ? colors.success.main + "04"
                : colors.background.light + "40",
              "&:hover": {
                borderColor: file ? colors.success.main : colors.primary.main,
                backgroundColor: file
                  ? colors.success.main + "08"
                  : colors.primary.main + "04",
                boxShadow: "none",
              },
            }}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            <input
              type="file"
              accept=".csv"
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                backgroundColor:
                  (file ? colors.success.main : colors.primary.main) + "10",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
              }}>
              {file ? (
                <FiCheckCircle size={48} color={colors.success.main} />
              ) : (
                <FiUpload size={48} color={colors.primary.main} />
              )}
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.text.primary,
                mb: 1.5,
                fontSize: "1.75rem",
              }}>
              {file ? "File selected & ready" : "Select a CSV file to upload"}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                mb: 4,
                maxWidth: 450,
                lineHeight: 1.6,
              }}>
              {file ? (
                <>
                  Selected file: <strong>{file.name}</strong>
                  <br />
                  {(file.size / 1024).toFixed(2)} KB
                </>
              ) : (
                "Drag and drop your contact file here or click the button below to browse your computer."
              )}
            </Typography>

            <Button
              variant="contained"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "4px",
                backgroundColor: file
                  ? colors.success.main
                  : colors.primary.main,
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: file
                    ? colors.success.dark
                    : colors.primary.dark,
                  boxShadow: "none",
                },
              }}>
              {file ? "Change File" : "Browse Files"}
            </Button>

            {!file && (
              <Button
                variant="text"
                startIcon={<FiArrowDownCircle />}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadSampleTemplate();
                }}
                sx={{
                  mt: 5,
                  fontSize: "0.875rem",
                  py: 0.5,
                  px: 1.5,
                  background: "none",
                  color: colors.primary.main,
                  textTransform: "none",
                  "&:hover": {
                    color: colors.primary.dark,
                    background: "none",
                  },
                }}>
                Download Sample Template
              </Button>
            )}

            {!file && (
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    fontSize: "0.85rem",
                  }}>
                  Maximum 2,000 rows per upload
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.hint || colors.text.secondary,
                    fontSize: "0.75rem",
                  }}>
                  Required columns: type, first_name/organization_name,
                  primary_email OR phone_1
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {uploading && (
          <Box sx={{ py: 10, textAlign: "center", width: "100%" }}>
            <Box sx={{ position: "relative", display: "inline-flex", mb: 4 }}>
              <CircularProgress
                variant="determinate"
                value={progress}
                size={120}
                thickness={2}
                sx={{ color: colors.primary.main }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <Typography
                  variant="h4"
                  component="div"
                  color="text.secondary"
                  fontWeight={700}>
                  {`${Math.round(progress)}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Uploading your contacts...
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Please keep this page open while we sync your data.
            </Typography>
            <Box sx={{ maxWidth: 400, mx: "auto" }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: colors.primary.main + "20",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {results && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor:
                    results.failed === 0
                      ? colors.success.main + "10"
                      : colors.warning.main + "10",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}>
                {results.failed === 0 ? (
                  <FiCheckCircle size={40} color={colors.success.main} />
                ) : (
                  <FiAlertCircle size={40} color={colors.warning.main} />
                )}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {results.failed === 0
                  ? "Upload Successful!"
                  : "Upload Completed with Warnings"}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                We've finished processing your contact file.
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 640, mx: "auto", mb: 8 }}>
              <Grid container spacing={3}>
                <Grid item display="flex" xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: "4px",
                      borderColor: colors.border.main,
                      backgroundColor: colors.background.light + "20",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        mb: 1.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.5,
                      }}>
                      TOTAL PROCESSED
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, color: colors.text.primary }}>
                      {results.total}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item display="flex" xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: "4px",
                      borderColor: colors.success.main + "40",
                      backgroundColor: colors.success.main + "05",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{
                        mb: 1.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.5,
                      }}>
                      SUCCESSFUL
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: colors.success.main,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                      }}>
                      {results.success}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item display="flex" xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: "4px",
                      borderColor:
                        results.failed > 0
                          ? colors.error.main + "40"
                          : colors.border.main,
                      backgroundColor:
                        results.failed > 0
                          ? colors.error.main + "05"
                          : "transparent",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Typography
                      variant="caption"
                      color={
                        results.failed > 0 ? "error.main" : "textSecondary"
                      }
                      sx={{
                        mb: 1.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.5,
                      }}>
                      FAILED
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color:
                          results.failed > 0
                            ? colors.error.main
                            : colors.text.primary,
                      }}>
                      {results.failed}
                    </Typography>
                    {results.failed > 0 && (
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        startIcon={<FiArrowDownCircle />}
                        onClick={downloadErrorCsv}
                        sx={{ mt: 1, fontWeight: 600, textTransform: "none" }}>
                        Errors
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={reset}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "4px",
                  fontWeight: 600,
                  borderColor: colors.border.main,
                  color: colors.text.primary,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: colors.text.primary,
                    backgroundColor: colors.background.light,
                  },
                }}>
                Upload Another
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/contacts")}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "4px",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: colors.primary.main,
                  "&:hover": { backgroundColor: colors.primary.dark },
                  boxShadow: "0 4px 14px 0 rgba(90, 106, 207, 0.39)",
                }}>
                Go to Contacts
              </Button>
            </Box>
          </Box>
        )}

        {!results && !uploading && file && (
          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 2,
              justifyContent: "center",
              width: "100%",
            }}>
            <Button
              variant="outlined"
              onClick={() => setFile(null)}
              sx={{
                px: 4,
                py: 1.2,
                borderRadius: "4px",
                fontWeight: 600,
                textTransform: "none",
                borderColor: colors.border.main,
                color: colors.text.primary,
                "&:hover": {
                  borderColor: colors.text.primary,
                  backgroundColor: colors.background.light,
                },
              }}>
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={processUpload}
              sx={{
                px: 6,
                py: 1.2,
                borderRadius: "4px",
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: colors.primary.main,
                "&:hover": { backgroundColor: colors.primary.dark },
                boxShadow: "0 4px 14px 0 rgba(90, 106, 207, 0.39)",
              }}>
              Start Upload
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
